/* eslint-disable @typescript-eslint/naming-convention */
import { Loading } from '@brands/Components/LoadingSpinner/Loading';
import PatientIntakeNavigation from '@brands/Components/PatientIntakeNavigation/PatientIntakeNavigation';
import { medicalConditionQuestions } from '@brands/Dashboard/MedicalHistory/MedicalCondition/utils/questions';
import { MedicalConditionSubmit } from '@brands/Dashboard/MedicalHistory/MedicalCondition/utils/types';
import { useWindowSize } from '@brands/hooks';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { useSnackbar } from '@brands/hooks/useStackbar';
import { addClinicalData } from '@brands/services/clinical_data/addClinicalData';
import { listClinicalData } from '@brands/services/clinical_data/listClinicalData';
import { updateClinicalData } from '@brands/services/clinical_data/updateClinicalData';
import { selectPatientForm } from '@brands/store/selectors/patientForm';
import { displayErrorDetails } from '@brands/Utils/displayError';
import GenericSnackbar from '@brands/Utils/GenericSnackbar';
import { yupResolver } from '@hookform/resolvers/yup';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { Button } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';

import Container from '../../../../../Components/Container/Container';
import QuestionAndRadioButton from '../../../../../Components/QuestionAndAnswers/QuestionAndRadioButtonForMed/QuestionAndRadioButton';
import { useMedicalHistoryOptional } from '@brands/Dashboard/PatientForms/PatientForm/utils/clinicalEnablementFlow';
import styles from './styles.module.scss';
import { validationSchema } from './utils/validationSchema';

const ClinicalDataStepOne = (): JSX.Element => {
  const screenSize = useWindowSize();
  const navigate = useNavigate();
  const roleNavigate = useRoleNavigate();
  const { values, selectedPatient } = useAppSelector(selectPatientForm);
  const selectedPatientId = selectedPatient?.value;
  const bypassRequired = useMedicalHistoryOptional(values);

  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [conditionsObjectId, setConditionsObjectId] = useState<number | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const { state, pathname } = useLocation();
  const isIntakeProcess = useMemo(() => {
    return state?.isIntakeProcess || pathname.includes('clinical-data') || false;
  }, [state]);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { isValid },
    watch,
    trigger,
    reset,
    getValues,
  } = useForm<MedicalConditionSubmit>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
  });

  const onSubmit = async (data: MedicalConditionSubmit): Promise<void> => {
    const hasAnswer =
      [
        data.arthritis,
        data.cancer,
        data.dementia_alzheimers,
        data.diabetes,
        data.heart_disease,
        data.dry_mouth,
        data.obesity,
        data.pregnancy_or_breastfeeding,
        data.respiratory_conditions,
        data.stroke,
        data.medical_condition_none_of_the_above,
      ].some((value) => value === true) ||
      (typeof data.conditions_not_listed === 'string' && data.conditions_not_listed.trim() !== '');

    if (bypassRequired && !hasAnswer) {
      sessionStorage.removeItem('medicalHistoryData');
      roleNavigate('/clinical-data/step-two');
      return;
    }

    try {
      setLoading(true);
      setHasUnsavedChanges(false);
      const {
        arthritis,
        cancer,
        dementia_alzheimers,
        diabetes,
        heart_disease,
        dry_mouth,
        obesity,
        pregnancy_or_breastfeeding,
        respiratory_conditions,
        stroke,
        conditions_not_listed,
        medical_condition_none_of_the_above,
      } = data;

      const medicalConditionPayload: MedicalConditionSubmit = {
        user_id: selectedPatientId as number,
        arthritis,
        cancer,
        dementia_alzheimers,
        diabetes,
        heart_disease,
        dry_mouth,
        obesity,
        pregnancy_or_breastfeeding,
        respiratory_conditions,
        stroke,
        conditions_not_listed: conditions_not_listed === 'false' ? '' : conditions_not_listed,
        none_of_the_above: medical_condition_none_of_the_above,
      };

      if (!conditionsObjectId) {
        await addClinicalData('medical_conditions', medicalConditionPayload);
      } else {
        await updateClinicalData('medical_conditions', conditionsObjectId.toString(), medicalConditionPayload);
      }

      reset(watch());
      if (!isIntakeProcess) {
        showSnackbar('Medical conditions saved successfully!', 'success');
      }
      sessionStorage.removeItem('medicalHistoryData');
      setLoading(false);
      roleNavigate('/clinical-data/step-two');
    } catch (error) {
      setHasUnsavedChanges(true);
      displayErrorDetails(error);
    }
  };

  const onInvalid = (e: any): void => {
    displayErrorDetails(e);
  };

  useEffect(() => {
    let isActive = true;

    const hydrateClinicalData = async (): Promise<void> => {
      if (!selectedPatientId) {
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const savedDraft = sessionStorage.getItem('medicalHistoryData');
        let parsedData;
        if (savedDraft) {
          parsedData = JSON.parse(savedDraft);
        } else {
          const res = await listClinicalData(selectedPatientId);
          parsedData = res.medical_conditions;
        }

        if (!isActive) return;

        if (parsedData) {
          setConditionsObjectId(parsedData.id || null);
          setValue('arthritis', parsedData.arthritis || false, { shouldValidate: true });
          setValue('cancer', parsedData.cancer || false, { shouldValidate: true });
          setValue('dementia_alzheimers', parsedData.dementia_alzheimers || false, {
            shouldValidate: true,
          });
          setValue('diabetes', parsedData.diabetes || false, { shouldValidate: true });
          setValue('heart_disease', parsedData.heart_disease || false, { shouldValidate: true });
          setValue('dry_mouth', parsedData.dry_mouth || false, { shouldValidate: true });
          setValue('obesity', parsedData.obesity || false, { shouldValidate: true });
          setValue('pregnancy_or_breastfeeding', parsedData.pregnancy_or_breastfeeding || false, {
            shouldValidate: true,
          });
          setValue('respiratory_conditions', parsedData.respiratory_conditions || false, {
            shouldValidate: true,
          });
          setValue('stroke', parsedData.stroke || false, { shouldValidate: true });
          setValue(
            'conditions_not_listed',
            parsedData.conditions_not_listed === 'true' ? '' : parsedData.conditions_not_listed || '',
            { shouldValidate: true },
          );
          setValue('medical_condition_none_of_the_above', parsedData.none_of_the_above || false, {
            shouldValidate: true,
          });
        }
        trigger();
      } catch (error) {
        if (isActive) {
          displayErrorDetails(error);
        }
      } finally {
        if (isActive) {
          setLoading(false);
          setHasUnsavedChanges(true);
        }
      }
    };

    hydrateClinicalData();

    return () => {
      isActive = false;
    };
  }, [selectedPatientId, setValue, trigger]);

  const saveFormData = useCallback((): void => {
    if (hasUnsavedChanges && !loading) {
      const currentValues = {
        ...getValues(),
        id: conditionsObjectId,
        none_of_the_above: getValues('medical_condition_none_of_the_above'),
      };
      sessionStorage.setItem('medicalHistoryData', JSON.stringify(currentValues));
    }
  }, [hasUnsavedChanges, getValues, conditionsObjectId, loading]);

  useEffect(() => {
    return () => {
      saveFormData();
    };
  }, [saveFormData]);

  useEffect(() => {
    // Handle page refresh/close
    const handleBeforeUnload = (): void => {
      saveFormData();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [saveFormData]);

  useEffect(() => {
    const noneOfTheAbove = watch('medical_condition_none_of_the_above');
    if (noneOfTheAbove) {
      setValue('arthritis', false, { shouldValidate: true });
      setValue('cancer', false, { shouldValidate: true });
      setValue('dementia_alzheimers', false, { shouldValidate: true });
      setValue('diabetes', false, { shouldValidate: true });
      setValue('heart_disease', false, { shouldValidate: true });
      setValue('dry_mouth', false, { shouldValidate: true });
      setValue('obesity', false, { shouldValidate: true });
      setValue('pregnancy_or_breastfeeding', false, { shouldValidate: true });
      setValue('respiratory_conditions', false, { shouldValidate: true });
      setValue('stroke', false, { shouldValidate: true });
      setValue('conditions_not_listed', '', { shouldValidate: true });
    }
  }, [watch('medical_condition_none_of_the_above')]);

  if (loading) {
    return <Loading fullScreen />;
  }

  return (
    <Container
      isVisibleOnMobile
      containerStyle={{
        justifyContent: screenSize.width >= 564 ? 'center' : 'start',
        overflow: 'hidden',
      }}
      childrenStyle={{
        zIndex: '2',
        paddingTop: screenSize.width > 768 ? '40px' : '16px',
        paddingBottom: screenSize.width > 768 ? '40px' : '16px',
      }}
      isSidenavVisible={false}
      isIntakeProcess
    >
      <div className={`fs-unmask ${styles.clinicalDataIntroContainer}`}>
        <PatientIntakeNavigation progress={20} />
        <form className={`${styles.formContainer}`} onSubmit={handleSubmit(onSubmit, onInvalid)}>
          <div className={styles.clinicalDataIntroWrapper}>
            <div>
              <div className={styles.clinicalDataIntroTitle}>
                <span>Do you have, or have you had, any of the following medical conditions?</span>
                {!bypassRequired && <span className={styles.clinicalDataIntroTitleRequired}> *</span>}
              </div>
            </div>
            <div className={styles.clinicalDataIntroBody}>
              <div className={styles.formContent}>
                {[...medicalConditionQuestions].map((element) => (
                  <QuestionAndRadioButton<MedicalConditionSubmit>
                    key={element.id}
                    element={element}
                    value={watch(element.id as keyof MedicalConditionSubmit)}
                    setValue={setValue}
                    trigger={trigger}
                    isMedicalHistory
                  />
                ))}
              </div>
            </div>
            <div className={styles.clinicalDataIntroFooter}>
              <Button variant="text" className={styles.clinicalDataIntroFooterBack} onClick={() => navigate(-1)}>
                <ArrowBackIosNewIcon />
                Back
              </Button>
              <Button
                variant="contained"
                className={styles.clinicalDataIntroFooterNext}
                type="button"
                disabled={loading || (!isValid && !bypassRequired)}
                onClick={() => {
                  if (isValid) {
                    handleSubmit(onSubmit, onInvalid)();
                  } else {
                    onSubmit(getValues());
                  }
                }}
              >
                Next
              </Button>
            </div>
          </div>
        </form>
      </div>
      <GenericSnackbar open={snackbar.isOpen} message={snackbar.message} type={snackbar.type} onClose={closeSnackbar} />
    </Container>
  );
};
export default ClinicalDataStepOne;
